/** @jsxImportSource react */
import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react'
import { useParams, Link } from 'react-router-dom'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { toast } from '../components/ui/Toast'
import { useAuth } from '../contexts/auth'
import { api } from '../services/api'
import { Order, OrderStatus, UserRole, Quote, User, ShippingType, PaymentStatus } from '../types'
import { useTranslation } from '../i18n'
import {
  Package,
  Clock,
  CheckCircle,
  DollarSign,
  Ship,
  X,
  FileText,
  User as UserIcon,
  Truck,
  MapPin,
  ExternalLink,
  Clipboard,
  MessageSquare,
  Edit,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Loader,
  ArrowLeft,
  Download,
} from 'lucide-react'
import { ChatSection } from '../components/ChatSection'
import { LinkPreview } from '../components/LinkPreview'
import { InvoiceTemplate } from '../components/InvoiceTemplate'
import html2pdf from 'html2pdf.js';

// Helper Functions
// FIX: Moved getTrackingLink to module scope to be accessible by multiple components.
const getTrackingLink = (carrier?: string, trackingNumber?: string) => {
  if (!carrier || !trackingNumber) return '#';
  const lowerCaseCarrier = carrier.toLowerCase();
  
  if (lowerCaseCarrier.includes('ups')) {
    return `https://www.ups.com/track?tracknum=${trackingNumber}`;
  }
  if (lowerCaseCarrier.includes('dhl')) {
    return `https://www.dhl.com/en/express/tracking.html?AWB=${trackingNumber}`;
  }
  if (lowerCaseCarrier.includes('fedex')) {
    return `https://www.fedextrack/?trknbr=${trackingNumber}`;
  }
  // Default to a generic Google search
  return `https://www.google.com/search?q=${carrier}+tracking+${trackingNumber}`;
};

const getStatusColor = (status: OrderStatus) => {
  switch (status) {
    case OrderStatus.Received:
      return 'bg-slate-100 text-slate-700'
    case OrderStatus.PriceChecking:
      return 'bg-blue-100 text-blue-700'
    case OrderStatus.Quoted:
      return 'bg-secondary-100 text-secondary-700'
    case OrderStatus.Confirmed:
      return 'bg-primary-100 text-primary-700'
    case OrderStatus.Purchased:
      return 'bg-indigo-100 text-indigo-700'
    case OrderStatus.Shipped:
      return 'bg-amber-100 text-amber-700'
    case OrderStatus.Delivered:
      return 'bg-emerald-100 text-emerald-700'
    case OrderStatus.Cancelled:
      return 'bg-rose-100 text-rose-700'
    default:
      return 'bg-slate-100 text-slate-700'
  }
}
const getStatusIcon = (status: OrderStatus) => {
  switch (status) {
    case OrderStatus.Received:
      return <Clock className="h-4 w-4" />
    case OrderStatus.PriceChecking:
      return <Clipboard className="h-4 w-4" />
    case OrderStatus.Quoted:
      return <FileText className="h-4 w-4" />
    case OrderStatus.Confirmed:
      return <CheckCircle className="h-4 w-4" />
    case OrderStatus.Purchased:
      return <DollarSign className="h-4 w-4" />
    case OrderStatus.Shipped:
      return <Ship className="h-4 w-4" />
    case OrderStatus.Delivered:
      return <Package className="h-4 w-4" />
    case OrderStatus.Cancelled:
      return <X className="h-4 w-4" />
    default:
      return <Clock className="h-4 w-4" />
  }
}

// Status Badge Component
const StatusBadge = ({ status }: { status: OrderStatus }) => {
  const { t } = useTranslation();
  return (
    <div
      className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 w-fit ${getStatusColor(status)}`}
    >
      {getStatusIcon(status)}
      <span>{t(status.toLowerCase())}</span>
    </div>
  )
}

// Order Timeline Component
const OrderTimeline = ({ order }: { order: Order }) => {
  const { t, formatDate } = useTranslation()
  const statuses = Object.values(OrderStatus)
  const statusTimestamps = useMemo(() => {
    const map = new Map<OrderStatus, string>()
    if (order.statusHistory) {
      for (const history of order.statusHistory) {
        map.set(history.status, history.timestamp)
      }
    }
    return map
  }, [order.statusHistory])
  

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200/70 p-6 transform transition-all duration-300 hover:shadow-md">
      <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
        <Clipboard className="h-5 w-5 text-primary-500" />
        {t('orderProgress') || 'Order Progress'}
      </h3>
      <div className="relative border-s border-slate-200 ms-4 mt-8">
        {statuses.map((status) => {
          const timestamp = statusTimestamps.get(status)
          const isCompleted = timestamp !== undefined
           // Skip PriceChecking in visual timeline if it didn't happen or isn't relevant for client view
           if (status === OrderStatus.PriceChecking && !timestamp) {
              return null;
           }

          return (
            <div key={status} className="mb-8 ms-6">
              <span
                className={`absolute flex items-center justify-center w-10 h-10 rounded-full -start-5 ring-4 ring-white ${isCompleted ? 'bg-primary-500 shadow-md' : 'bg-slate-200'} transition-all duration-300`}
              >
                {isCompleted ? (
                  <CheckCircle className="w-5 h-5 text-white" />
                ) : (
                  getStatusIcon(status)
                )}
              </span>
              <div className={`ps-6 ${isCompleted ? '' : 'opacity-50'}`}>
                <h4
                  className={`text-base font-semibold ${isCompleted ? 'text-slate-900' : 'text-slate-500'}`}
                >
                  {t(status.toLowerCase())}
                </h4>
                {timestamp && (
                  <p className="text-sm text-slate-500 mt-1">
                    {formatDate(timestamp, { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                )}
                {status === OrderStatus.Shipped &&
                  order.trackingNumber &&
                  isCompleted && (
                    <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                      <p className="font-semibold text-blue-800 mb-2">
                        {t('trackingInfo') || 'Tracking Information'}
                      </p>
                      <p className="mb-1">
                        <strong>{t('carrier') || 'Carrier'}:</strong>{' '}
                        {order.carrier}
                      </p>
                      <p className="flex items-center gap-2">
                        <strong>
                          {t('trackingNumber') || 'Tracking Number'}:
                        </strong>
                        <a
                          href={getTrackingLink(
                            order.carrier,
                            order.trackingNumber,
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-700 hover:underline inline-flex items-center"
                        >
                          {order.trackingNumber}
                          <ExternalLink className="h-3.5 w-3.5 ms-1" />
                        </a>
                      </p>
                    </div>
                  )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const QuoteForm = ({ orderId }: { orderId: string }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [supplier, setSupplier] = useState('')
  const [price, setPrice] = useState('')
  const [shippingCost, setShippingCost] = useState('')
  const [deliveryTime, setDeliveryTime] = useState('')

  const resetForm = () => {
    setSupplier('')
    setPrice('')
    setShippingCost('')
    setDeliveryTime('')
  }
  const addQuoteMutation = useMutation({
    mutationFn: (newQuote: Quote) => api.addQuoteToOrder(orderId, newQuote),
    onSuccess: () => {
      toast.success(t('quoteAddedSuccess') || 'Quote added successfully!')
      queryClient.invalidateQueries({
        queryKey: ['order', orderId],
      })
      resetForm()
    },
    onError: (error: Error) => {
        let errorMessage = t('quoteAddError') || 'Failed to add quote';
        if ((error as any).code === 'storage/unauthorized') {
            errorMessage = t('storageUnauthorizedError');
        } else if (error.message.includes('Please wait')) {
            errorMessage = error.message;
        } else if (error.message) {
            errorMessage = `${t('quoteAddError')}: ${error.message}`;
        }
      toast.error(errorMessage);
    },
  })
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user || !price || parseFloat(price) <= 0 || !supplier) {
      toast.error(
        t('fillRequiredFieldsError') || 'Please fill all required fields',
      )
      return
    }
    
    const newQuote: Quote = {
      id: `quote-${Date.now()}`,
      staffId: user.id,
      supplier,
      price: parseFloat(price),
      shippingCost: parseFloat(shippingCost) || 0,
      deliveryTime,
      createdAt: new Date().toISOString(),
    }
    addQuoteMutation.mutate(newQuote)
  }
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200/70 p-6 transform transition-all duration-300 hover:shadow-md mt-5">
      <h3 className="font-bold text-lg text-slate-800 mb-5 flex items-center gap-2">
        <DollarSign className="h-5 w-5 text-primary-500" />
        {t('addQuote') || 'Add Quote'}
      </h3>
      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 md:grid-cols-2 gap-5"
      >
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-slate-700">
            {t('supplierName') || 'Supplier Name'} *
          </label>
           <div className="relative">
                <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                    <UserIcon size={18} className="text-slate-400" />
                </div>
                <input
                    type="text"
                    placeholder={t('supplierNamePlaceholder')}
                    value={supplier}
                    onChange={(e) => setSupplier(e.target.value)}
                    required
                    className={`w-full p-3 ps-10 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200`}
                />
            </div>
        </div>
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-slate-700">
            {t('price') || 'Price'} *
          </label>
           <div className="relative">
                <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                    <DollarSign size={18} className="text-slate-400" />
                </div>
                <input
                    type="number"
                    placeholder={t('pricePlaceholder')}
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    required
                    min="0.01"
                    step="0.01"
                    className={`w-full p-3 ps-10 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200`}
                />
            </div>
        </div>
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-slate-700">
            {t('shippingCost') || 'Shipping Cost'}
          </label>
          <div className="relative">
                <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                    <Truck size={18} className="text-slate-400" />
                </div>
                <input
                    type="number"
                    placeholder={t('shippingCostPlaceholder')}
                    value={shippingCost}
                    onChange={(e) => setShippingCost(e.target.value)}
                    min="0"
                    step="0.01"
                    className={`w-full p-3 ps-10 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200`}
                />
            </div>
        </div>
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-slate-700">
            {t('deliveryTime') || 'Delivery Time'}
          </label>
          <div className="relative">
                <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                    <Clock size={18} className="text-slate-400" />
                </div>
                <input
                    type="text"
                    placeholder={t('deliveryTimePlaceholder')}
                    value={deliveryTime}
                    onChange={(e) => setDeliveryTime(e.target.value)}
                    className={`w-full p-3 ps-10 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200`}
                />
            </div>
        </div>
        <div className="md:col-span-2 mt-4">
          <button
            type="submit"
            disabled={addQuoteMutation.isPending}
            className="w-full py-3 px-4 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-lg font-medium hover:from-primary-700 hover:to-primary-600 transition-all duration-300 shadow-sm flex items-center justify-center transform hover:translate-y-[-2px]"
          >
            {addQuoteMutation.isPending ? (
              <>
                <Loader className="w-4 h-4 me-2 animate-spin" />
                {t('submitting') || 'Submitting...'}
              </>
            ) : (
              <>
                <DollarSign className="w-4 h-4 me-2" />
                {t('addQuote') || 'Add Quote'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

const QuotesList = ({ order, allUsers }: { order: Order, allUsers: User[] }) => {
  const { t, formatCurrency, formatDate } = useTranslation()
  const { user } = useAuth()
  const [expandedQuotes, setExpandedQuotes] = useState<Record<string, boolean>>(
    {},
  )
  const toggleQuote = (quoteId: string) => {
    setExpandedQuotes((prev) => ({
      ...prev,
      [quoteId]: !prev[quoteId],
    }))
  }

  const getStaffName = (staffId: string) => {
    return allUsers.find((u) => u.id === staffId)?.name || staffId
  }
  if (!order.quotes || order.quotes.length === 0) {
    return (
      <div className="p-10 text-center bg-slate-50 rounded-lg border border-dashed border-slate-300">
        <FileText className="h-12 w-12 text-slate-400 mx-auto mb-3" />
        <p className="text-slate-500 font-medium">
          {t('noQuotesYet') || 'No quotes have been added yet.'}
        </p>
      </div>
    )
  }
  return (
    <div className="space-y-5">
      {order.quotes.map((quote, index) => {
        const isExpanded = expandedQuotes[quote.id] || false
        // Mask supplier name for clients
        const displaySupplier = user?.role === UserRole.Client 
            ? `${t('quote') || 'Quote'} #${index + 1}` 
            : quote.supplier;

        return (
          <div
            key={quote.id}
            className="bg-white border border-slate-200/70 rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md"
          >
            <div className="p-5 flex justify-between items-start gap-4">
              <div className="flex-grow">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-slate-800 text-lg">
                    {displaySupplier}
                  </h4>
                  <div className="text-xl font-bold text-primary-600">
                    {formatCurrency(quote.price + quote.shippingCost)}
                  </div>
                </div>
                <div className="mt-3 text-sm grid grid-cols-2 gap-x-4 gap-y-1">
                  <div className="flex justify-between">
                    <span className="text-slate-500">
                      {t('listPrice') || 'Price'}:
                    </span>
                    <span className="font-medium">
                      {formatCurrency(quote.price)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">
                      {t('listShipping') || 'Shipping'}:
                    </span>
                    <span className="font-medium">
                      {formatCurrency(quote.shippingCost)}
                    </span>
                  </div>
                </div>
                {!isExpanded && (
                  <div className="flex justify-between items-center mt-4">
                    <span className="text-sm text-slate-500">
                      {t('deliveryTime') || 'Delivery Time'}:{' '}
                      {quote.deliveryTime || 'N/A'}
                    </span>
                    <button
                      onClick={() => toggleQuote(quote.id)}
                      className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1 transition-all duration-200 hover:gap-2"
                    >
                      {t('viewMore') || 'View More'}
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            {isExpanded && (
              <div className="border-t border-slate-100 p-5 bg-slate-50">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <h5 className="text-sm font-semibold text-slate-700 mb-3">
                      {t('quoteDetails') || 'Quote Details'}
                    </h5>
                    <p className="text-sm mb-2">
                      <span className="text-slate-500">
                        {t('deliveryTime') || 'Delivery Time'}:
                      </span>{' '}
                      <span className="font-medium">
                        {quote.deliveryTime || 'N/A'}
                      </span>
                    </p>
                    {user?.role !== UserRole.Client && (
                        <p className="text-sm mb-2">
                        <span className="text-slate-500">
                            {t('addedBy') || 'Added By'}:
                        </span>{' '}
                        <span className="font-medium">
                            {getStaffName(quote.staffId)}
                        </span>
                        </p>
                    )}
                    <p className="text-sm">
                      <span className="text-slate-500">
                        {t('addedOn') || 'Added On'}:
                      </span>{' '}
                      <span className="font-medium">
                        {formatDate(quote.createdAt, { year: 'numeric', month: 'numeric', day: 'numeric' })}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="text-end mt-4">
                  <button
                    onClick={() => toggleQuote(quote.id)}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium inline-flex items-center gap-1 transition-all duration-200 hover:gap-2"
                  >
                    {t('showLess') || 'Show Less'}
                    <ChevronUp className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

const AdminControls = ({ order, allUsers }: { order: Order, allUsers: User[] }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const isShippingOrder = order.orderType === 'Shipping'
  const [status, setStatus] = useState(order.status)
  const [paymentStatus, setPaymentStatus] = useState(order.paymentStatus)
  const [finalPrice, setFinalPrice] = useState(
    order.finalPrice?.toString() || '',
  )
  const [commission, setCommission] = useState(
    order.commission?.toString() || '',
  )
  const [trackingNumber, setTrackingNumber] = useState(
    order.trackingNumber || '',
  )
  const [carrier, setCarrier] = useState(order.carrier || '')
  const [shippingType, setShippingType] = useState(order.shippingType || '')
  const [assignedStaffId, setAssignedStaffId] = useState(
    order.assignedStaffId || '',
  )

  useEffect(() => {
    setStatus(order.status)
    setPaymentStatus(order.paymentStatus)
    setFinalPrice(order.finalPrice?.toString() || '')
    setCommission(order.commission?.toString() || '')
    setTrackingNumber(order.trackingNumber || '')
    setCarrier(order.carrier || '')
    setShippingType(order.shippingType || '')
    setAssignedStaffId(order.assignedStaffId || '')
  }, [order])

  const updateMutation = useMutation({
    mutationFn: (updates: Partial<Order>) => api.updateOrder(order.id, updates),
    onSuccess: () => {
      toast.success(t('orderUpdateSuccess') || 'Order updated successfully!')
      queryClient.invalidateQueries({
        queryKey: ['order', order.id],
      })
    },
    onError: () => {
      toast.error(t('orderUpdateError') || 'Failed to update order.')
    },
  })
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const updates: Partial<Order> = {
      status,
      paymentStatus,
      finalPrice: finalPrice ? parseFloat(finalPrice) : undefined,
      commission: commission ? parseFloat(commission) : undefined,
      trackingNumber: trackingNumber || undefined,
      carrier: carrier || undefined,
      shippingType: (shippingType as ShippingType) || undefined,
      assignedStaffId: assignedStaffId || undefined,
    }
    updateMutation.mutate(updates)
  }

  const staffUsers = useMemo(() => {
    return allUsers.filter((u) => u.role === UserRole.Staff)
  }, [allUsers])
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200/70 p-6 transform transition-all duration-300 hover:shadow-md">
      <h3 className="font-bold text-lg text-slate-800 mb-5 flex items-center gap-2">
        <Edit className="h-5 w-5 text-primary-500" />
        {t('manageOrder') || 'Manage Order'}
      </h3>
      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 md:grid-cols-2 gap-5"
      >
        <div className="space-y-1.5">
          <label
            htmlFor="status"
            className="block text-sm font-medium text-slate-700"
          >
            {t('status') || 'Status'}
          </label>
          <select
            id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value as OrderStatus)}
            className="w-full p-3 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white transition-all duration-200"
          >
            {Object.values(OrderStatus).map((s) => (
              <option key={s} value={s}>
                {t(s.toLowerCase())}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <label
            htmlFor="paymentStatus"
            className="block text-sm font-medium text-slate-700"
          >
            {t('paymentStatus') || 'Payment Status'}
          </label>
          <select
            id="paymentStatus"
            value={paymentStatus}
            onChange={(e) => setPaymentStatus(e.target.value as PaymentStatus)}
            className="w-full p-3 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white transition-all duration-200"
          >
            {Object.values(PaymentStatus).map((s) => (
                <option key={s} value={s}>{t(s.toLowerCase())}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <label
            htmlFor="finalPrice"
            className="block text-sm font-medium text-slate-700"
          >
            {isShippingOrder
              ? t('shippingFee') || 'Shipping Fee'
              : t('finalPrice') || 'Final Price'}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
              <DollarSign className="h-4 w-4 text-slate-500" />
            </div>
            <input
              id="finalPrice"
              type="number"
              value={finalPrice}
              onChange={(e) => setFinalPrice(e.target.value)}
              placeholder="e.g., 1500"
              min="0"
              step="0.01"
              className="w-full ps-9 p-3 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
            />
          </div>
        </div>
        {!isShippingOrder && (
          <div className="space-y-1.5">
            <label
              htmlFor="commission"
              className="block text-sm font-medium text-slate-700"
            >
              {t('commission') || 'Commission'}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
                <DollarSign className="h-4 w-4 text-slate-500" />
              </div>
              <input
                id="commission"
                type="number"
                value={commission}
                onChange={(e) => setCommission(e.target.value)}
                placeholder="e.g., 100"
                min="0"
                step="0.01"
                className="w-full ps-9 p-3 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
              />
            </div>
          </div>
        )}
        <div className="space-y-1.5">
          <label
            htmlFor="carrier"
            className="block text-sm font-medium text-slate-700"
          >
            {t('carrier') || 'Carrier'}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
              <Truck className="h-4 w-4 text-slate-500" />
            </div>
            <input
              id="carrier"
              type="text"
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              placeholder="e.g., UPS, DHL"
              className="w-full ps-9 p-3 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <label
            htmlFor="trackingNumber"
            className="block text-sm font-medium text-slate-700"
          >
            {t('trackingNumber') || 'Tracking Number'}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
              <Package className="h-4 w-4 text-slate-500" />
            </div>
            <input
              id="trackingNumber"
              type="text"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="e.g., 1Z..."
              className="w-full ps-9 p-3 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <label
            htmlFor="shippingType"
            className="block text-sm font-medium text-slate-700"
          >
            {t('shippingType') || 'Shipping Type'}
          </label>
          <select
            id="shippingType"
            value={shippingType}
            onChange={(e) => setShippingType(e.target.value)}
            className="w-full p-3 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white transition-all duration-200"
          >
            <option value="">{t('notSet')}</option>
            <option value="Air">{t('byAir') || 'By Air'}</option>
            <option value="Sea">{t('bySea') || 'By Sea'}</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <label
            htmlFor="assignedStaffId"
            className="block text-sm font-medium text-slate-700"
          >
            {t('assignToStaff') || 'Assign to Staff'}
          </label>
          <select
            id="assignedStaffId"
            value={assignedStaffId}
            onChange={(e) => setAssignedStaffId(e.target.value)}
            className="w-full p-3 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white transition-all duration-200"
          >
            <option value="">{t('unassigned') || 'Unassigned'}</option>
            {staffUsers.map((staff) => (
              <option key={staff.id} value={staff.id}>
                {staff.name}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2 mt-4">
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="w-full py-3 px-4 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-lg font-medium hover:from-primary-700 hover:to-primary-600 transition-all duration-300 shadow-sm flex items-center justify-center transform hover:translate-y-[-2px]"
          >
            {updateMutation.isPending ? (
              <>
                <Loader className="w-4 h-4 me-2 animate-spin" />
                {t('saving') || 'Saving...'}
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 me-2" />
                {t('saveChanges') || 'Save Changes'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

const OrderDetailsSkeleton = () => (
  <div className="bg-white rounded-xl border border-slate-200/70 shadow-sm p-6 animate-pulse">
    <div className="flex justify-between items-start">
      <div>
        <div className="h-8 w-64 bg-slate-200 rounded mb-2"></div>
        <div className="h-6 w-32 bg-slate-200 rounded"></div>
      </div>
      <div className="text-end">
        <div className="h-7 w-24 bg-slate-200 rounded mb-2"></div>
        <div className="h-5 w-20 bg-slate-200 rounded"></div>
      </div>
    </div>
    <div className="mt-8 border-t border-slate-200 pt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <div className="h-6 w-40 bg-slate-200 rounded mb-4"></div>
        <div className="h-5 w-full bg-slate-200 rounded mb-2"></div>
        <div className="h-5 w-3/4 bg-slate-200 rounded mb-2"></div>
        <div className="h-5 w-full bg-slate-200 rounded"></div>
      </div>
      <div>
        <div className="h-6 w-32 bg-slate-200 rounded mb-4"></div>
        <div className="h-5 w-full bg-slate-200 rounded mb-2"></div>
        <div className="h-5 w-3/4 bg-slate-200 rounded mb-2"></div>
        <div className="h-5 w-full bg-slate-200 rounded"></div>
      </div>
    </div>
    <div className="mt-8">
      <div className="h-6 w-40 bg-slate-200 rounded mb-4"></div>
      <div className="space-y-3">
        <div className="h-20 w-full bg-slate-200 rounded"></div>
        <div className="h-20 w-full bg-slate-200 rounded"></div>
        <div className="h-20 w-full bg-slate-200 rounded"></div>
      </div>
    </div>
  </div>
)

// Main Order Details Page Component
export const OrderDetailsPage = () => {
  const { id } = useParams<{
    id: string
  }>()
  const { user } = useAuth()
  const { t, formatDate, formatCurrency } = useTranslation()
  const queryClient = useQueryClient()
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const invoiceRef = useRef<HTMLDivElement>(null);
  
  // Real-time order fetching to ensure immediate updates for 'Show Quotes' toggle
  const [order, setOrder] = useState<Order | null>(null);
  const [isOrderLoading, setIsOrderLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setIsOrderLoading(true);
    const unsubscribe = api.listenToOrderById(id, (data) => {
      setOrder(data);
      setIsOrderLoading(false);
    });
    return () => unsubscribe();
  }, [id]);

  // Fetch users data
  const { data: allUsers = [], isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.getUsers(),
    enabled: user?.role !== UserRole.Client,
  })
  // Fetch warehouses data
  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses'],
    queryFn: api.getWarehouses,
  })
  
  const sourceWarehouse = useMemo(() => {
    if (!order?.sourceWarehouseId || warehouses.length === 0) return null
    return warehouses.find((w) => w.id === order.sourceWarehouseId)
  }, [order?.sourceWarehouseId, warehouses])
  
  const confirmOrderMutation = useMutation({
    mutationFn: () =>
      api.updateOrder(order!.id, {
        status: OrderStatus.Confirmed,
      }),
    onSuccess: () => {
      toast.success(t('orderConfirmed') || 'Order confirmed successfully')
      queryClient.invalidateQueries({
        queryKey: ['order', id],
      })
    },
    onError: () => {
      toast.error(t('orderConfirmError') || 'Failed to confirm order')
    },
  })
  
  const handleDownloadInvoice = async () => {
      if (!invoiceRef.current || !order) return;
      
      setIsGeneratingPdf(true);
      const element = invoiceRef.current;
      const opt = {
          margin: 0, // Margins are handled by the container padding in InvoiceTemplate
          filename: `Invoice_${order.orderId}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      try {
          await html2pdf().set(opt).from(element).save();
          toast.success(t('downloadStarted'));
      } catch (error) {
          console.error("PDF Generation Failed", error);
          toast.error(t('invoiceError'));
      } finally {
          setIsGeneratingPdf(false);
      }
  };

  const isLoading = isOrderLoading || isLoadingUsers
  if (isLoading) return <OrderDetailsSkeleton />
  if (!order)
    return (
      <div className="bg-white rounded-xl border border-slate-200/70 shadow-sm p-8 text-center">
        <AlertCircle className="h-14 w-14 text-rose-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-slate-900 mb-3">
          {t('orderNotFound') || 'Order Not Found'}
        </h2>
        <p className="text-slate-500 mb-6">
          {t('orderNotFoundDesc') ||
            'The order you are looking for does not exist or has been removed.'}
        </p>
        <Link
          to="/orders"
          className="inline-flex items-center px-5 py-2.5 bg-primary-600 text-white rounded-lg font-medium transition-all duration-300 hover:bg-primary-700"
        >
          <ArrowLeft className="h-4 w-4 me-2 rtl:me-0 rtl:ms-2 rtl:rotate-180" />
          {t('backToOrders') || 'Back to Orders'}
        </Link>
      </div>
    )

  const assignedStaffName = order.assignedStaffId
    ? allUsers.find((u) => u.id === order.assignedStaffId)?.name
    : null
  const isShippingOrder = order.orderType === 'Shipping'
  // Find client: if current user is client, use them, otherwise find in allUsers
  const client = user?.role === UserRole.Client 
      ? user 
      : (allUsers.find((u) => u.id === order.clientId) || { name: 'Unknown Client', email: '', id: order.clientId } as User);

  const canManageOrder =
    user?.role === UserRole.Admin ||
    (user?.role === UserRole.Staff && order.assignedStaffId === user.id)

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Hidden Invoice Template for PDF Generation. 
          Fixed position off-screen ensures it renders but is hidden from user.
          Using visibility: hidden causes html2canvas to render blank. 
      */}
      <div style={{ position: 'fixed', top: 0, left: '-5000px', width: '210mm', zIndex: -1 }}>
          <InvoiceTemplate ref={invoiceRef} order={order} client={client} />
      </div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <Link
            to="/orders"
            className="inline-flex items-center text-slate-600 hover:text-primary-600 transition-colors mb-2"
          >
            <ArrowLeft className="h-4 w-4 me-1 rtl:rotate-180" />
            <span className="text-sm">
              {t('backToOrders') || 'Back to Orders'}
            </span>
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex flex-wrap items-center gap-2">
            <span>{order.productName}</span>
            <span className="text-primary-600 text-lg md:text-xl font-medium">
              ({order.orderId})
            </span>
          </h1>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <StatusBadge status={order.status} />
          <button
            onClick={handleDownloadInvoice}
            disabled={isGeneratingPdf}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors shadow-sm disabled:opacity-70 disabled:cursor-wait"
          >
            {isGeneratingPdf ? <Loader className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            <span>{isGeneratingPdf ? t('generating') : t('downloadInvoice')}</span>
          </button>
          {user?.role === UserRole.Client &&
            order.status === OrderStatus.Quoted &&
            order.finalPrice && (
              <button
                onClick={() => confirmOrderMutation.mutate()}
                disabled={confirmOrderMutation.isPending}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors shadow-sm flex items-center"
              >
                {confirmOrderMutation.isPending ? (
                  <Loader className="w-4 h-4 me-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 me-2" />
                )}
                {t('confirmPriceAndProceed') || 'Confirm Price & Proceed'}
              </button>
            )}
        </div>
      </div>
      
      {/* ... Rest of the layout grid ... */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Left Side */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Details Card */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200/70 p-6 transform transition-all duration-300 hover:shadow-md">
             {/* ... Order Details Content ... */}
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Clipboard className="h-5 w-5 text-primary-500" />
                {t('orderDetails') || 'Order Details'}
              </h2>
              <span className="text-sm text-slate-500">
                {formatDate(order.createdAt, { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-slate-500 block mb-1">
                      {t('orderType') || 'Order Type'}
                    </span>
                    <span className="font-medium">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {t(order.orderType.toLowerCase()) || order.orderType}
                      </span>
                    </span>
                  </div>
                  
                  <div>
                      <span className="text-sm text-slate-500 block mb-1">
                        {t('status') || 'Status'}
                    </span>
                     <StatusBadge status={order.status} />
                  </div>

                  <div>
                    <span className="text-sm text-slate-500 block mb-1">
                      {t('quantityLabel') || 'Quantity'}
                    </span>
                    <span className="font-medium">
                      {order.quantity || 'N/A'}
                    </span>
                  </div>
                  
                  <div>
                      <span className="text-sm text-slate-500 block mb-1">
                        {t('assignedTo') || 'Assigned To'}
                      </span>
                      <span className="font-medium">
                        {assignedStaffName || (order.assignedStaffId ? (t('staffMember') || 'Staff Member') : (
                          <span className="text-amber-600">
                            {t('unassigned') || 'Unassigned'}
                          </span>
                        ))}
                      </span>
                  </div>
                  
                  {order.shippingType && (
                    <div>
                        <span className="text-sm text-slate-500 block mb-1">
                            {t('shippingType') || 'Shipping Type'}
                        </span>
                        <span className="font-medium">
                            {t(order.shippingType.toLowerCase())}
                        </span>
                    </div>
                  )}

                  {order.productLink && (
                    <div>
                      <span className="text-sm text-slate-500 block mb-1">
                        {t('productLink') || 'Product Link'}
                      </span>
                      <div className="mt-2">
                        <LinkPreview url={order.productLink} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-slate-500 block mb-1">
                      {t('paymentStatus') || 'Payment Status'}
                    </span>
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${order.paymentStatus === 'Verified' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}
                    >
                      {t(order.paymentStatus.toLowerCase())}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm text-slate-500 block mb-1">
                      {isShippingOrder
                        ? t('shippingFee') || 'Shipping Fee'
                        : t('finalPrice') || 'Final Price'}
                    </span>
                    <span className="text-xl font-bold text-primary-600">
                      {order.finalPrice
                        ? formatCurrency(order.finalPrice)
                        : 'N/A'}
                    </span>
                  </div>

                  {order.commission != null && (
                    <div>
                      <span className="text-sm text-slate-500 block mb-1">
                        {t('commission') || 'Commission'}
                      </span>
                      <span className="font-medium">
                          {formatCurrency(order.commission)}
                      </span>
                    </div>
                  )}

                  {order.carrier && (
                    <div>
                      <span className="text-sm text-slate-500 block mb-1">
                        {t('carrier') || 'Carrier'}
                      </span>
                      <span className="font-medium">
                        {order.carrier}
                      </span>
                    </div>
                  )}

                  {order.trackingNumber && (
                    <div>
                      <span className="text-sm text-slate-500 block mb-1">
                        {t('trackingNumber') || 'Tracking Number'}
                      </span>
                      <div className="flex items-center gap-2">
                        <a
                          href={getTrackingLink(order.carrier, order.trackingNumber)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-primary-600 hover:underline inline-flex items-center gap-1"
                        >
                          {order.trackingNumber}
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </div>
                    </div>
                  )}

                </div>
              </div>
            </div>
            {order.notes && (
              <div className="mt-5 pt-5 border-t border-slate-200">
                <span className="text-sm text-slate-500 block mb-2">
                  {t('notes') || 'Notes'}
                </span>
                <p className="bg-slate-50 p-4 rounded-lg text-slate-700 text-sm border border-slate-200/70">
                  {order.notes}
                </p>
              </div>
            )}
            {order.productImageUrl && (
              <div className="mt-5 pt-5 border-t border-slate-200">
                <span className="text-sm text-slate-500 block mb-2">
                  {t('clientUploadedImage') || 'Client Uploaded Image'}
                </span>
                {/\.(jpg|jpeg|png|gif|webp|svg|bmp)/i.test(order.productImageUrl) || order.productImageUrl.startsWith('data:image') ? (
                  <a
                    href={order.productImageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block overflow-hidden rounded-lg border border-slate-200/70 hover:opacity-90 transition-opacity"
                  >
                    <img
                      src={order.productImageUrl}
                      alt={order.productName}
                      className="w-full h-auto object-cover"
                      onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.parentElement!.innerHTML = `<div class="p-4 flex items-center justify-center bg-gray-100 text-gray-500"><span class="text-sm">View File</span></div>`;
                      }}
                    />
                  </a>
                ) : (
                   <a
                    href={order.productImageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center p-4 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors group"
                  >
                    <FileText className="h-8 w-8 text-primary-500 me-3 group-hover:scale-110 transition-transform" />
                    <div>
                        <p className="text-sm font-medium text-slate-900">{t('viewAttachedDocument') || 'View Attached Document'}</p>
                        <p className="text-xs text-slate-500 mt-1">{t('clickToOpen') || 'Click to open'}</p>
                    </div>
                    <ExternalLink className="h-4 w-4 ms-auto text-slate-400 group-hover:text-primary-500 transition-colors" />
                  </a>
                )}
              </div>
            )}
          </div>
          {/* Shipping Details */}
          {isShippingOrder && (order.weight || order.dimensions) && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200/70 p-6 transform transition-all duration-300 hover:shadow-md">
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2 mb-5">
                <Truck className="h-5 w-5 text-primary-500" />
                {t('shippingDetails') || 'Shipping Details'}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {order.weight != null && (
                  <div className="bg-blue-50 border border-blue-200/70 rounded-xl p-4 flex items-center">
                    <div className="bg-blue-100 rounded-full p-3 me-4">
                      <Truck className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-blue-700 font-semibold">
                        {t('weight') || 'Weight'}
                      </p>
                      <p className="text-xl font-bold text-slate-800">
                        {order.weight} kg
                      </p>
                    </div>
                  </div>
                )}
                {order.dimensions && (
                  <div className="bg-emerald-50 border border-emerald-200/70 rounded-xl p-4 flex items-center">
                    <div className="bg-emerald-100 rounded-full p-3 me-4">
                      <Package className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm text-emerald-700 font-semibold">
                        {t('dimensions') || 'Dimensions'}
                      </p>
                      <p className="text-xl font-bold text-slate-800">
                        {order.dimensions}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              {sourceWarehouse && (
                <div className="mt-5 pt-5 border-t border-slate-200">
                  <span className="text-sm text-slate-500 block mb-2">
                    {t('source') || 'Source'}
                  </span>
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-200/70 flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-800">
                        {sourceWarehouse.name}, {sourceWarehouse.country}
                      </p>
                      <p className="text-sm text-slate-500">
                        {sourceWarehouse.address}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Supplier Quotes - Always Visible */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200/70 p-6 transform transition-all duration-300 hover:shadow-md">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary-500" />
                {t('supplierQuotes') || 'Supplier Quotes'}
              </h2>
              {(user?.role === UserRole.Admin ||
                user?.role === UserRole.Staff) && (
                <span className="text-sm text-primary-600 font-medium">
                  {order.quotes.length} {t('quotes') || 'quotes'}
                </span>
              )}
            </div>
            <QuotesList order={order} allUsers={allUsers} />
            {canManageOrder && <QuoteForm orderId={order.id} />}
          </div>
           {/* Chat Section */}
          {(user?.role === UserRole.Admin ||
            user?.role === UserRole.Staff ||
            user?.id === order.clientId) && (
            <div className="mt-6">
              <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-primary-500" />
                {t('order_chat') || 'Messages'}
              </h2>
               <ChatSection orderId={order.id} />
            </div>
          )}
        </div>
        {/* Sidebar - Right Side */}
        <div className="space-y-6">
          {/* Client Information */}
          {(user?.role === UserRole.Admin || user?.role === UserRole.Staff) &&
            client && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200/70 p-6 transform transition-all duration-300 hover:shadow-md">
                <h2 className="text-lg font-bold text-slate-800 mb-5 flex items-center gap-2">
                  <UserIcon className="h-5 w-5 text-primary-500" />
                  {t('clientInformation') || 'Client Information'}
                </h2>
                <div className="space-y-4">
                  <div>
                    <span className="text-sm text-slate-500 block mb-1">
                      {t('name') || 'Name'}
                    </span>
                    <span className="font-medium">{client.name}</span>
                  </div>
                  <div>
                    <span className="text-sm text-slate-500 block mb-1">
                      {t('teCode') || 'TE Code'}
                    </span>
                    <span className="font-medium">
                      {client.teCode || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm text-slate-500 block mb-1">
                      {t('email') || 'Email'}
                    </span>
                    <a
                      href={`mailto:${client.email}`}
                      className="font-medium text-primary-600 hover:underline"
                    >
                      {client.email}
                    </a>
                  </div>
                  {client.phone && (
                    <div>
                      <span className="text-sm text-slate-500 block mb-1">
                        {t('phone') || 'Phone'}
                      </span>
                      <span className="font-medium">{client.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          {/* Client Action Buttons */}
          {user?.role === UserRole.Client &&
            order.status === OrderStatus.Quoted &&
            order.finalPrice && (
              <div className="bg-primary-50 rounded-xl shadow-sm border border-primary-200/70 p-6 transform transition-all duration-300 hover:shadow-md">
                <h3 className="font-semibold text-primary-800 mb-3">
                  {t('readyToConfirm') || 'Ready to Confirm?'}
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  {t('confirmOrderMessage') ||
                    'By confirming this order, you agree to the final price and our terms of service.'}
                </p>
                <button
                  onClick={() => confirmOrderMutation.mutate()}
                  disabled={confirmOrderMutation.isPending}
                  className="w-full py-3 px-4 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-lg font-medium hover:from-primary-700 hover:to-primary-600 transition-all duration-300 shadow-sm flex items-center justify-center transform hover:translate-y-[-2px]"
                >
                  {confirmOrderMutation.isPending ? (
                    <>
                      <Loader className="w-4 h-4 me-2 animate-spin" />
                      {t('confirming') || 'Confirming...'}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 me-2" />
                      {t('confirmPriceAndProceed') || 'Confirm Price & Proceed'}
                    </>
                  )}
                </button>
              </div>
            )}
          {/* Admin Controls */}
          {canManageOrder && (
            <AdminControls order={order} allUsers={allUsers} />
          )}
          {/* Order Timeline */}
          <OrderTimeline order={order} />
        </div>
      </div>
    </div>
  )
}